import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Save } from "lucide-react";
import { usePublishCourseMutation } from "../../services/course/courseApi";
import Loader from "../common/Loader";

const CourseDetailsActionsSection = ({
  progress = 65,
  remainingFields = 8,
  canPublish = true,
  missingFields = [],
  courseId,
  courseData,
  hasChanges = false,
  onSave,
  isSaving=false
}) => {
  const navigate = useNavigate();
  const [triggerPublish, publishResult] = usePublishCourseMutation();
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: null,
    message: "",
  });

  const isAlreadyPublished = Boolean(courseData?.isCoursePublished);

  const isPublishing = publishResult.isLoading || publishResult.isFetching;

  const handleModalClose = () => {
    const wasSuccess = modalState.type === "success";
    setModalState({ ...modalState, isOpen: false });

    if (wasSuccess) {
      navigate("/admin/course-management");
    }
  };

  const handleAction = async () => {
    if (!courseId) {
      setModalState({
        isOpen: true,
        type: "error",
        message: "Course ID not provided.",
      });

      return;
    }

    try {
      // ==========================================
      // STEP 1: Unsaved changes hain
      // ==========================================
      if (hasChanges) {
       

        const saveResult = await onSave?.();

        

        // Save API fail hui
        if (!saveResult) {
          return;
        }

        return;
      }

      // ==========================================
      // STEP 2: No changes -> Publish
      // ==========================================
      if (isAlreadyPublished) {
        return;
      }

      if (!canPublish) {
        setModalState({
          isOpen: true,
          type: "error",
          message:
            missingFields.length > 0
              ? `Please complete the following before publishing: ${missingFields.join(", ")}.`
              : "This course is not ready to be published yet.",
        });

        return;
      }

      const result = await triggerPublish(courseId).unwrap();

      if (result?.success === false) {
        throw new Error(result?.message || "Failed to publish course.");
      }

      setModalState({
        isOpen: true,
        type: "success",
        message: result?.message || "Course published successfully.",
      });
    } catch (error) {
      console.error("Action failed:", error);


      setModalState({
        isOpen: true,
        type: "error",
        message:
          error?.data?.message || error?.message || "Something went wrong.",
      });
    }
  };

  const isBusy = isSaving || isPublishing;
  const isPublishDisabled = !hasChanges && isAlreadyPublished;
  return (
    <>
      <section className="mt-6 w-full max-w-[1401px] border border-[#E5E7EB] bg-white px-4 py-4 shadow-[0_4px_6px_-1px_#0000001A] sm:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#923D8C1A] text-sm font-bold text-[#923D8C] sm:h-12 sm:w-12 sm:text-base">
              {progress}%
            </div>

            <div className="min-w-0">
              <p className="text-sm text-[#000]">Course Completion</p>
              <p className="text-xs text-[#6B7280]">
                {remainingFields} required fields remaining
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleAction}
            disabled={!courseId || isBusy || isPublishDisabled}
            className={`inline-flex h-11 items-center justify-center gap-1 rounded px-5 text-sm transition sm:text-base ${
              courseId && !isBusy && !isPublishDisabled
                ? "bg-[#923D8C] text-white hover:opacity-95"
                : "bg-[#923D8C]/60 text-white cursor-not-allowed"
            }`}
          >
            {hasChanges ? (
              <Save size={18} />
            ) : (
              <Check size={18} strokeWidth={2.5} />
            )}

            <span>
              {isSaving
                ? "Saving..."
                : isPublishing
                  ? "Publishing..."
                  : hasChanges
                    ? "Save Changes"
                    : isAlreadyPublished
                      ? "Published"
                      : "Publish Course"}
            </span>
          </button>
        </div>
      </section>

      {isPublishing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Loader size="lg" />
        </div>
      )}

      {modalState.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 py-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={handleModalClose}
          />
          <div className="relative z-10 w-full max-w-[420px] rounded bg-white p-5 shadow-lg sm:p-6">
            <h3 className="text-lg font-semibold">
              {modalState.type === "success" ? "Success" : "Error"}
            </h3>
            <p className="mt-2 text-sm text-gray-700">{modalState.message}</p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleModalClose}
                className="inline-flex h-9 w-full items-center justify-center rounded bg-[#923D8C] px-4 text-sm text-white sm:w-auto"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CourseDetailsActionsSection;
